import { classifySlotOccupancy } from "./scheduleGridModel";
import ScheduleType from "./ScheduleType";
import type {
  AgendaCitySummary,
  AgendaCommercialRegion,
  AgendaConsultant,
  AgendaDayKpis,
  AgendaDayPayload,
  AgendaFiltersState,
  AgendaGridSlot,
  AgendaWeekDaySummary,
  AgendaWeekSummary,
} from "./agendaTypes";
import { isoDateToDisplayDate } from "./agendaDateFormat";
import { getAgendaWeekStripIsoDates } from "./agendaWeekDateRange";
import { getFortalezaTodayIso } from "./agendaFiltersQuery";

export type LegacySchedulesPayload = {
  dates?: string[];
  bussiness_hours?: string[];
  commercial_region?: AgendaCommercialRegion[];
  consultants?: AgendaConsultant[];
  schedules?: AgendaGridSlot[];
};

const WEEKDAY_SHORT = ["DOM", "SEG", "TER", "QUA", "QUI", "SEX", "SÁB"];

function displayDateToIso(displayDate: string): string | null {
  const match = displayDate.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!match) {
    return null;
  }
  const [, day, month, year] = match;
  return `${year}-${month}-${day}`;
}

function scheduleMatchesIsoDate(scheduleDate: string, isoDate: string): boolean {
  return scheduleDate.startsWith(isoDate);
}

function applyAgendaFilters(
  schedules: AgendaGridSlot[],
  consultants: AgendaConsultant[],
  filters: AgendaFiltersState
): { schedules: AgendaGridSlot[]; consultants: AgendaConsultant[] } {
  let nextConsultants = consultants;
  let nextSchedules = schedules;

  if (filters.city) {
    const cityLower = filters.city.toLowerCase();
    nextConsultants = nextConsultants.filter(
      (consultant) => consultant.city.toLowerCase() === cityLower
    );
    const consultantIds = new Set(nextConsultants.map((item) => item.id));
    nextSchedules = nextSchedules.filter((item) =>
      consultantIds.has(item.consultant_id)
    );
  }

  if (filters.consultantId != null) {
    nextConsultants = nextConsultants.filter(
      (consultant) => consultant.id === filters.consultantId
    );
    nextSchedules = nextSchedules.filter(
      (item) => item.consultant_id === filters.consultantId
    );
  }

  if (filters.visitType === "presential") {
    nextSchedules = nextSchedules.filter((item) => item.video_call === 0);
  } else if (filters.visitType === "video") {
    nextSchedules = nextSchedules.filter((item) => item.video_call === 1);
  }

  return { schedules: nextSchedules, consultants: nextConsultants };
}

function countVisitsForIsoDate(
  schedules: AgendaGridSlot[],
  isoDate: string
): number {
  return schedules.filter(
    (item) =>
      scheduleMatchesIsoDate(item.date, isoDate) &&
      item.status === ScheduleType.SCHEDULED
  ).length;
}

function buildCitySummaries(
  consultants: AgendaConsultant[],
  schedules: AgendaGridSlot[],
  businessHours: string[],
  isoDate: string
): AgendaCitySummary[] {
  const byCity = new Map<string, AgendaConsultant[]>();
  consultants.forEach((consultant) => {
    const list = byCity.get(consultant.city) ?? [];
    list.push(consultant);
    byCity.set(consultant.city, list);
  });

  const summaries: AgendaCitySummary[] = [];
  byCity.forEach((cityConsultants, name) => {
    let totalSlots = 0;
    let occupiedSlots = 0;

    businessHours.forEach((hour) => {
      cityConsultants.forEach((consultant) => {
        const hourKey = hour.split(":")[0];
        const cellSchedules = schedules.filter(
          (item) =>
            scheduleMatchesIsoDate(item.date, isoDate) &&
            item.consultant_id === consultant.id &&
            item.date.includes(`${hourKey}:`)
        );
        const kind = classifySlotOccupancy(cellSchedules);
        if (kind !== "blocked") {
          totalSlots += 1;
        }
        if (kind === "scheduled") {
          occupiedSlots += 1;
        }
      });
    });

    summaries.push({
      name,
      consultant_count: cityConsultants.length,
      occupied_slots: occupiedSlots,
      total_slots: totalSlots,
    });
  });

  return summaries;
}

function computeLegacyKpis(
  schedules: AgendaGridSlot[],
  consultants: AgendaConsultant[],
  businessHours: string[],
  isoDate: string
): AgendaDayKpis {
  const daySchedules = schedules.filter((item) =>
    scheduleMatchesIsoDate(item.date, isoDate)
  );
  const visits = daySchedules.filter(
    (item) => item.status === ScheduleType.SCHEDULED
  );
  const inPerson = visits.filter((item) => item.video_call === 0).length;
  const video = visits.filter((item) => item.video_call === 1).length;

  let totalSlots = 0;
  let occupied = 0;
  let freeSlots: { time: string; consultantName: string }[] = [];

  const nowFortaleza = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Fortaleza",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(new Date());
  const [nowHour, nowMinute] = nowFortaleza.split(":").map(Number);
  const nowMinutes = nowHour * 60 + nowMinute;
  const windowEnd = nowMinutes + 180;

  businessHours.forEach((hour) => {
    const [hourPart, minutePart] = hour.split(":");
    const slotMinutes = Number(hourPart) * 60 + Number(minutePart ?? 0);

    consultants.forEach((consultant) => {
      const cellSchedules = daySchedules.filter(
        (item) =>
          item.consultant_id === consultant.id &&
          item.date.includes(`${hourPart}:`)
      );
      // Hours without a row were never unlocked, so they count as blocked.
      const kind = classifySlotOccupancy(cellSchedules);
      const isFree =
        cellSchedules.length > 0 &&
        cellSchedules.every((item) => item.status === ScheduleType.UNLOCKED);

      if (kind !== "blocked") {
        totalSlots += 1;
      }
      if (kind === "scheduled") {
        occupied += 1;
      }

      if (
        isFree &&
        slotMinutes >= nowMinutes &&
        slotMinutes <= windowEnd
      ) {
        freeSlots.push({
          time: hour.slice(0, 5),
          consultantName: consultant.first_name,
        });
      }
    });
  });

  freeSlots.sort((a, b) => a.time.localeCompare(b.time));

  const completed = visits.filter(
    (item) => item.has_card || item.outcome === "completed"
  ).length;

  return {
    visits_today: {
      total: visits.length,
      in_person: inPerson,
      video,
      trend_vs_same_weekday: null,
    },
    occupancy: {
      percent: totalSlots > 0 ? Math.round((occupied / totalSlots) * 100) : 0,
      occupied,
      total: totalSlots,
    },
    completed: {
      completed,
      expected: visits.length,
      no_show: visits.filter((item) => item.outcome === "no_show").length,
      pending_post_visit: Math.max(0, visits.length - completed),
    },
    free_next_3h: {
      count: freeSlots.length,
      earliest_time: freeSlots[0]?.time ?? null,
      earliest_consultant_name: freeSlots[0]?.consultantName ?? null,
    },
  };
}

export function adaptLegacySchedulesToDayPayload(
  raw: LegacySchedulesPayload,
  filters: AgendaFiltersState
): AgendaDayPayload {
  const businessHours = Array.isArray(raw.bussiness_hours)
    ? raw.bussiness_hours
    : [];
  const regions = Array.isArray(raw.commercial_region)
    ? [...raw.commercial_region].sort(
        (a, b) => a.relevance_order - b.relevance_order
      )
    : [];
  const consultants = Array.isArray(raw.consultants) ? raw.consultants : [];
  const schedules = Array.isArray(raw.schedules) ? raw.schedules : [];

  const filtered = applyAgendaFilters(schedules, consultants, filters);

  const kpis = computeLegacyKpis(
    filtered.schedules,
    filtered.consultants,
    businessHours,
    filters.date
  );

  const cities = buildCitySummaries(
    filtered.consultants,
    filtered.schedules,
    businessHours,
    filters.date
  );

  return {
    date: filters.date,
    business_hours: businessHours,
    commercial_region: regions,
    consultants: filtered.consultants,
    cities,
    schedules: filtered.schedules.filter((item) =>
      scheduleMatchesIsoDate(item.date, filters.date)
    ),
    kpis,
    has_open_hours: businessHours.length > 0,
  };
}

function buildWeekDaySummary(isoDate: string, visitsCount: number): AgendaWeekDaySummary {
  const [year, month, day] = isoDate.split("-").map(Number);
  const date = new Date(year, month - 1, day);
  return {
    date: isoDate,
    weekday_label: WEEKDAY_SHORT[date.getDay()],
    day_of_month: String(day).padStart(2, "0"),
    visits_count: visitsCount,
  };
}

export function buildWeekSummaryFromLegacy(
  raw: LegacySchedulesPayload,
  anchorTodayIso: string = getFortalezaTodayIso(),
  schedules: AgendaGridSlot[]
): AgendaWeekSummary {
  const isoDates = getAgendaWeekStripIsoDates(anchorTodayIso);

  return {
    days: isoDates.map((isoDate) =>
      buildWeekDaySummary(isoDate, countVisitsForIsoDate(schedules, isoDate))
    ),
  };
}

export function isAgendaEndpointUnavailable(error: unknown): boolean {
  const status = (error as { response?: { status?: number } })?.response?.status;
  return status === 403 || status === 404 || status === 405;
}

export { isoDateToDisplayDate };
